import {
  Box,
  Button,
  ButtonGroup,
  Fab,
  Grid,
  Pagination,
  styled,
  Typography,
} from '@mui/material';

import LayoutBase from '../../shared/layouts';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { api } from '../../shared/services/api';
import Cookies from 'js-cookie';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import ModalDetail from './components/modalDetails';
import { IResponseAjud } from '../../shared/services/types/response';
import CardAjud from './components/cardAjud';
import ModalGenerateShipment from './components/modalGenerateShipment';
import InputFilter from './components/InputFilter';
import ModalBlock from './components/modalBlock';
import ModalTransfer from './components/modalTransfer';
import Loading from '../../components/loading';

const filtersObject = {
  Todos: 'ALL',
  Criado: 'CREATED',
  Pendente: 'PENDING',
  Deferido: 'DONE',
  Cancelado: 'CANCEL',
  Recusado: 'REFUSED',
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const Ajud = () => {
  const token = Cookies.get('@token');
  const [ajudData, setAjudData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IResponseAjud | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);
  const [filter, setFilter] = useState<string>('ALL');

  const [typeFilter, setTypeFilter] = useState<string | number>('');
  const [generateModal, setGenerateModal] = useState<boolean>(false);
  const itemsPerPage = 8;

  // Atualiza a página ao mudar o filtro ou a página
  useEffect(() => {
    handleGetAll();
  }, [currentPage, filter, typeFilter]);

  interface TypeRecord {
    value: string;
    label: string;
  }

  const tipos: TypeRecord[] = [
    { value: '', label: 'Todos' },
    { value: '05', label: 'Desbloqueio' },
    { value: '07', label: 'Transferência' },
    { value: '03', label: 'Bloqueio' },
  ];

  const handleTypeFilterChange = (value: string | number) => {
    setTypeFilter(value);
  };

  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleGetAll = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        limit: itemsPerPage,
        page: currentPage,
        status: filter !== 'ALL' ? filter : undefined, // Envia filtro apenas se não for "ALL"
        ...(typeFilter !== '' && { type_record: typeFilter }), // Adiciona tipo_record somente se typeFilter não for vazio
      },
    };

    try {
      setIsLoading(true);
      const responseDetailPerson = await api.get('/ajudArchive/getAll', config);

      const {
        data,
        meta: { totalPages },
      } = responseDetailPerson.data;

      setAjudData(data || []);
      setPages(totalPages || 1);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  const handleUploadAjudArchive = async (files: FileList | null) => {
    setIsUploadLoading(true);

    if (!files || files?.length === 0) {
      setIsUploadLoading(false);
      return;
    }

    if (files[0].type != 'text/plain') {
      toast.warn('Tipo de arquivo inválido');
      setIsUploadLoading(false);
      return;
    }

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    };

    const formDataBody = new FormData();
    const ajudArchive = files?.[0];

    formDataBody.append('file', ajudArchive);
    try {
      const responseAjudArchive = await api.post(
        '/archive/upload-ajud',
        formDataBody,
        config,
      );

      if (responseAjudArchive.status === 201) {
        toast.success('Upload feito com sucesso');
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      handleGetAll();
      setIsUploadLoading(false);
    }
    setIsUploadLoading(false);
  };

  const handleOpenModal = (item: IResponseAjud) => {
    setSelectedItem(item);
  };

  return (
    <LayoutBase
      footer={
        isLoading || ajudData.length === 0 ? null : (
          <Pagination
            count={pages}
            page={currentPage}
            onChange={handleChange}
            color="primary"
          />
        )
      }
    >
      <Typography color="textPrimary" variant="h5">
        AJUD
      </Typography>
      <Typography sx={{ marginBottom: 2 }}>Remessa de Bloqueio</Typography>
      <Grid
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: {
            xs: 'column', // Mobile: empilha em coluna
            sm: 'row', // A partir do "sm": em linha (padrão)
          },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Fab
            component="label"
            size="medium"
            color="secondary"
            aria-label="add"
            disabled={isUploadLoading}
          >
            <AddIcon />
            <VisuallyHiddenInput
              type="file"
              onChange={(event) => handleUploadAjudArchive(event.target.files)}
              multiple
            />
          </Fab>

          <ButtonGroup
            variant="outlined"
            color="primary"
            // sx={{ marginBottom: 2, marginLeft: 2 }}
          >
            {Object.entries(filtersObject).map(([label, value]) => (
              <Button
                key={value}
                variant={filter === value ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange(value)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        <InputFilter
          label="Escolha uma opção"
          options={tipos}
          value={typeFilter}
          onChange={handleTypeFilterChange}
        />
      </Grid>
      {filter == 'PENDING' && ajudData.length > 0 && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setGenerateModal(true)}
          sx={{
            fontSize: {
              xs: '0.75rem',
              sm: '0.875rem',
              marginLeft: 8,
              marginTop: 20,
            },
          }}
        >
          Gerar remessa de bloqueio
        </Button>
      )}
      <Loading isLoading={isLoading} />

      {ajudData.length === 0 && !isLoading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={UndrawNoData}
              alt="Svg em construção"
              sx={{
                maxWidth: '15%',
                height: 'auto',
                marginTop: 10,
              }}
            />
          </Box>
          <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
            Dados não encontrados...
          </Typography>
        </>
      ) : (
        <Box sx={{ padding: 3 }}>
          <Grid container spacing={2}>
            {ajudData.map((item, index) => (
              <>
                <CardAjud
                  key={index}
                  ajudValue={item}
                  handleOpenModal={() => handleOpenModal(item)}
                />
              </>
            ))}
          </Grid>
          {selectedItem?.type_record === '07' && (
            <ModalTransfer
              open={selectedItem !== null}
              ajudValue={selectedItem}
              closeModal={(shouldReload) => {
                setSelectedItem(null);
                if (shouldReload) handleGetAll();
              }}
            />
          )}

          {selectedItem?.type_record === '05' && (
            <ModalBlock
              open={selectedItem !== null}
              ajudValue={selectedItem}
              closeModal={(shouldReload) => {
                setSelectedItem(null);
                if (shouldReload) handleGetAll();
              }}
            />
          )}

          {selectedItem !== null &&
            selectedItem?.type_record !== '05' &&
            selectedItem?.type_record !== '07' && (
              <ModalDetail
                open={selectedItem !== null}
                ajudValue={selectedItem}
                closeModal={(shouldReload) => {
                  setSelectedItem(null);
                  if (shouldReload) handleGetAll();
                }}
              />
            )}
        </Box>
      )}
      <ModalGenerateShipment
        open={generateModal}
        closeModal={(shouldReload?: boolean) => {
          setGenerateModal(false);
          if (shouldReload) handleGetAll();
        }}
      />
    </LayoutBase>
  );
};

export default Ajud;
